<template>
    <div>
        <div class="row" v-if="propertyAvailable('valid_from') || propertyAvailable('valid_to')">
            <div class="col-md-6" v-if="propertyAvailable('valid_from')">
                <datepicker
                    v-model="valid_from"
                    :disabled="readonly || !playbackPrivilege"
                    :label="translations.valid_from"
                    :max="validFromMaxLimit"
                    hideDetails
                    :placeholder="!inner_properties.validityDetached ? validFromPlaceholderLocalized : null" />
            </div>
            <div class="col-md-6" v-if="propertyAvailable('valid_to')">
                <datepicker
                    v-model="valid_to"
                    :disabled="readonly || !playbackPrivilege"
                    :label="translations.valid_to"
                    :min="validToMinLimit"
                    hideDetails
                    :placeholder="!inner_properties.validityDetached ? validToPlaceholderLocalized : null"
                    align-right />
            </div>
        </div>

        <div class="row" v-if="propertyAvailable('valid_time_from') || propertyAvailable('valid_time_to')">
            <div class="col-md-6" v-if="propertyAvailable('valid_time_from')">
                <timepicker
                    :id="'valid-time-from-' + inner_properties['index']"
                    :label="translations.from()"
                    v-model="valid_time_from"
                    picker-family="valid-time"
                    :disabled="readonly || !playbackPrivilege"
                />
            </div>
            <div class="col-md-6" v-if="propertyAvailable('valid_time_to')">
                <timepicker
                    :id="'valid-time-to-' + inner_properties['index']"
                    :label="translations.to()"
                    v-model="valid_time_to"
                    align-right
                    picker-family="valid-time"
                    :disabled="readonly || !playbackPrivilege"
                />
            </div>
        </div>

        <div class="row" v-if="propertyAvailable('duration_type') || propertyAvailable('duration') || propertyAvailable('display_runs')">
            <div id="select-duration_type" class="col-md-8" v-if="propertyAvailable('duration_type')">
                <v-select
                    v-model="inner_properties.duration_type"
                    :label="translations.duration"
                    :disabled="readonly || !playbackPrivilege"
                    :items="[
                        {value: 2, text: translations.duration_element + (inner_duration_element === '?' ? '' : ' (' + inner_duration_element + 's)'), disabled: !propertyAvailable('display_runs')},
                        {value: 1, text: translations.duration_default},
                        {value: 0, text: translations.duration_free}
                    ]"
                    attach="#select-duration_type .v-select"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </v-select>
            </div>
            <div class="col-md-4" v-if="propertyAvailable('display_runs') && inner_properties.duration_type === 2">
                <number-field
                    v-model="inner_properties.display_runs"
                    :label="translations.display_runs"
                    :disabled="readonly || !playbackPrivilege"
                    :limits="getFieldLimits('display_runs')"
                    :hint="translations.display_runs_description"
                    :min="1"
                    :max="86400"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </number-field>
            </div>
            <div class="col-md-4" v-if="propertyAvailable('duration') && inner_properties.duration_type !== 2">
                <number-field
                    v-model="inner_duration"
                    :label="translations.seconds"
                    :disabled="inner_properties.duration_type === 1 || readonly || !playbackPrivilege"
                    :limits="getFieldLimits('duration')"
                    :min="1"
                    :max="86400"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </number-field>
            </div>
        </div>

        <div class="row" v-if="propertyAvailable('loop') || propertyAvailable('weekdays')">
            <div :class="propertyAvailable('weekdays') ? 'col-md-6' : 'col-md-12'" v-if="propertyAvailable('loop')">
                <number-field
                    v-model="inner_properties.loop"
                    :label="translations.loop"
                    :disabled="readonly || !playbackPrivilege"
                    :limits="getFieldLimits('loop')"
                    :hint="translations.loop_description"
                    :min="1"
                    :max="86400"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </number-field>
            </div>
            <div id="select-loop" :class="propertyAvailable('loop') ? 'col-md-6' : 'col-md-12'" v-if="propertyAvailable('weekdays')">
                <v-autocomplete
                    v-model="inner_properties.weekdays"
                    :label="translations.hide_weekdays"
                    :disabled="readonly || !playbackPrivilege"
                    :items="weekdays_all"
                    multiple
                    attach="#select-loop .v-select"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </v-autocomplete>
            </div>
        </div>


        <div class="row" v-if="propertyAvailable('default_duration')">
            <div class="col-md-12">
                <number-field
                    v-model="inner_default_duration"
                    :label="translations.duration_default + ' (' + translations.seconds + ')'"
                    :disabled="readonly || !playbackPrivilege"
                    :min="1"
                    :max="86400"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </number-field>
            </div>
        </div>

        <div class="row">
            <div id="select-transition" :class="inner_properties.transition !== '1' ? 'col-md-6' : 'col-md-12'" v-if="propertyAvailable('transition')">
                <v-autocomplete
                    v-model="inner_properties.transition"
                    :label="translations.transition"
                    :disabled="readonly || !playbackPrivilege"
                    :items="transitions"
                    attach="#select-transition .v-select"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </v-autocomplete>
            </div>
            <div class="col-md-6" v-if="inner_properties.transition !== '1' && propertyAvailable('transition_length')">
                <number-field
                    v-model="inner_properties.transition_length"
                    :label="translations.transition_length"
                    :disabled="readonly || !playbackPrivilege"
                    :limits="getFieldLimits('transition_length')"
                    placeholder="0.4"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </number-field>
            </div>
        </div>

        <div class="row">
            <div id="select-synchronize_playback" class="col-md-12" v-if="propertyAvailable('synchronize_playback') &&
                (propertyAvailable('synchronize_playback')['value'] === 'rich' || propertyAvailable('synchronize_playback')['value'] === 'readonly')">
                <v-select
                    v-model="inner_properties.synchronize_playback"
                    :label="translations.synchronize_playback"
                    :hint="translations.synchronize_playback_description"
                    :disabled="(propertyAvailable('synchronize_playback')['value'] === 'rich' ? readonly : true) || !playbackPrivilege"
                    :items="[
                        {value: 0, text: translations.sync_disabled},
                        {value: 1, text: translations.sync_across_players},
                        {value: 2, text: translations.sync_interactive},
                    ]"
                    attach="#select-synchronize_playback .v-select"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </v-select>
            </div>
        </div>

        <div class="row">
            <div id="select-smart_loop" :class="[3, 4, 5, 6].includes(inner_properties.smart_loop) ? 'col-md-8' : 'col-md-12'" v-if="propertyAvailable('smart_loop')">
                <v-select
                    v-model="inner_properties.smart_loop"
                    :label="translations.smart_loop"
                    :disabled="readonly || !playbackPrivilege"
                    :items="[
                        {value: 0, text: translations.smart_loop_whole_list},
                        {value: 1, text: translations.smart_loop_interrupt},
                        {value: 2, text: translations.smart_loop_first},
                        {value: 3, text: translations.smart_loop_frequency},
                        {value: 4, text: translations.smart_loop_frequency_blocked},
                        {value: 5, text: translations.smart_loop_max_elements},
                        {value: 6, text: translations.smart_loop_max_duration},
                    ]"
                    @change="inner_properties.smart_loop_number=null"
                    attach="#select-smart_loop .v-select"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </v-select>
            </div>
            <div class="col-md-4" v-if="propertyAvailable('smart_loop_number') && [3, 4, 5, 6].includes(inner_properties.smart_loop)">
                <number-field
                    v-model="inner_properties.smart_loop_number"
                    :label="inner_properties.smart_loop === 5 ? translations.max_elements : (inner_properties.smart_loop === 6 ? translations.max_duration : translations.frequency)"
                    :disabled="readonly || !playbackPrivilege"
                    :limits="getFieldLimits('smart_loop_number')"
                    :placeholder="inner_properties.smart_loop === 5 ? '2' : (inner_properties.smart_loop === 6 ? '60' : '6')"
                    :outlined="outlined" :dense="dense" hide-details="auto">
                </number-field>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12" v-if="propertyAvailable('description')">
                <v-text-field
                    v-model="description"
                    :label="translations.description"
                    :disabled="readonly || !playbackPrivilege"
                    :outlined="outlined" :dense="dense" hide-details="auto"
                    v-on:keydown="changedDescription()"
                    v-on:blur="updateDescription()">
                </v-text-field>
            </div>
        </div>
    </div>
</template>

<script>
import commonDefinitionMixin from "./commonDefinitionMixin";
import utility from "../../../utility";
import { dateUSToDateISO, localizeDate } from '../../../../utils/date';

export default {
    components: {},
    mixins: [
        commonDefinitionMixin,
    ],
    props: [
        'value',
        'definition',
        'is_parent',
        'is_publish',
        'clearable',
        'readonly',
        'durationPrivilege',
        'playbackPrivilege',
        'default_duration'
    ],
    data: function () {
        return {
            inner_properties: {
                display_runs: undefined,
                validity: {},
            },
            transitions: [],
            description: '',
            timeout_id: null,
        }
    },
    computed: {
        translations() {
            return {
                valid_time: Lang.langGet("Show element..."),
                valid_time_description: Lang.langGet("Limits the element to be played between specific times. Set both to 00:00 to disable it."),
                valid_from: Lang.langGet("Not before"),
                valid_to: Lang.langGet("Not after"),
                display_runs: Lang.langGet("Runs"),
                display_runs_description: Lang.langGet("Indicates, how often the element is repeated with its own duration."),
                free_duration: Lang.langGet("Free duration"),
                transition: Lang.langChoice("Transition|Transitions", 1),
                transition_length: Lang.langGet("Transition length"),
                loop: Lang.langGet("Show every X loops"),
                loop_description: Lang.langGet("Indicates on which loop the element will be played. E.g., 2 means that the element will be played in one loop, then will not be played in the next, etc."),
                hide_weekdays: Lang.langGet("Skip weekdays"),
                duration: Lang.langGet("Duration"),
                duration_element: Lang.langGet("Element duration"),
                duration_default: Lang.langGet("Standard duration"),
                duration_free: Lang.langGet("Free duration"),
                seconds: Lang.langChoice("Second|Seconds", 2),
                yes: Lang.langGet("Yes"),
                no: Lang.langGet("No"),
                synchronize_playback: Lang.langGet("Sync settings"),
                synchronize_playback_description: Lang.langGet("Check system requirements in hardware.ini."),
                sync_disabled: Lang.langGet("Disabled"),
                sync_across_players: Lang.langGet("Sync across players"),
                sync_interactive: Lang.langGet("Interactive list"),
                smart_loop: Lang.langGet("Loop-Settings"),
                smart_loop_whole_list: Lang.langGet("Play the whole campaign all the way through"),
                smart_loop_interrupt: Lang.langGet("Interrupt after each event"),
                smart_loop_first: Lang.langGet("Always start with the first element"),
                smart_loop_frequency: Lang.langGet("Automatic frequency control"),
                smart_loop_frequency_blocked: Lang.langGet("Automatic frequency control (in blocks if necessary)"),
                smart_loop_max_elements: Lang.langGet("Up to ... elements in a row"),
                smart_loop_max_duration: Lang.langGet("Up to ... seconds in a row"),
                frequency: Lang.langGet("Frequency [1/h]"),
                max_elements: Lang.langGet("max. Elements"),
                max_duration: Lang.langGet("max. Duration [s]"),
                description: Lang.langGet("Description / Comment"),
                from() {
                    let string = Lang.langGet("Show from");
                    return string.charAt(0).toUpperCase() + string.slice(1);
                },
                to() {
                    let string = Lang.langGet("Show to");
                    return string.charAt(0).toUpperCase() + string.slice(1);
                },
            }
        },
        weekdays_all() {
            return [
                {
                    text: Lang.langGet("Monday"),
                    value: 'pdMonday'
                },
                {
                    text: Lang.langGet("Tuesday"),
                    value: 'pdTuesday'
                },
                {
                    text: Lang.langGet("Wednesday"),
                    value: 'pdWednesday'
                },
                {
                    text: Lang.langGet("Thursday"),
                    value: 'pdThursday'
                },
                {
                    text: Lang.langGet("Friday"),
                    value: 'pdFriday'
                },
                {
                    text: Lang.langGet("Saturday"),
                    value: 'pdSaturday'
                },
                {
                    text: Lang.langGet("Sunday"),
                    value: 'pdSunday'
                }
            ];
        },
        valid_from: {
            get() {
                const props = this.inner_properties;

                if (this.is_publish !== '0') return props.valid_from;
                if (this.is_parent) return props.parameters?.['valid-from'];

                return props.valid_from || props.validity.from;
            },
            set(val) {
                if (val && this.validToOrPlaceholder && (new Date(dateUSToDateISO(val)) > new Date(dateUSToDateISO(this.validToOrPlaceholder)))) {
                    this.valid_to = val;
                }

                const props = this.inner_properties;
                props.validityDetached = !!(val || this.valid_to);

                if (this.is_publish === '0' && this.is_parent) {
                    if (!props.parameters) props.parameters = {};
                    props.parameters['valid-from'] = val;
                    return;
                }

                props.valid_from = val;
                if (props.valid_to_placeholder && !props.valid_to && val) {
                    props.valid_to = props.valid_to_placeholder;
                }

                if (this.is_publish === '0') {
                    this.updateValidity();
                }
            }
        },
        valid_to: {
            get() {
                const props = this.inner_properties;

                if (this.is_publish !== '0') return props.valid_to;

                if (this.is_parent) return props.parameters?.['valid-to'];

                return props.valid_to || props.validity.to;
            },
            set(val) {
                if (val && this.validFromOrPlaceholder && (new Date(dateUSToDateISO(val)) < new Date(dateUSToDateISO(this.validFromOrPlaceholder)))) {
                    this.valid_from = val;
                }

                const props = this.inner_properties;
                props.validityDetached = !!(val || this.valid_from);

                if (this.is_publish === '0' && this.is_parent) {
                    if (!props.parameters) props.parameters = {};
                    props.parameters['valid-to'] = val;
                    return;
                }

                props.valid_to = val;
                if (props.valid_from_placeholder && !props.valid_from && val) {
                    props.valid_from = props.valid_from_placeholder;
                }

                if (this.is_publish === '0') {
                    this.updateValidity();
                }
            }
        },
        valid_time_from: {
            get: function () {
                return this.inner_properties.valid_time_from;
            },
            set: function (val) {
                this.inner_properties.valid_time_from = val;
            }
        },
        valid_time_to: {
            get: function () {
                return this.inner_properties.valid_time_to;
            },
            set: function (val) {
                this.inner_properties.valid_time_to = val;
            }
        },
        inner_loop: {
            get: function () {
                return this.innerFieldGetter('loop');
            },
            set: function (val) {
                this.innerFieldSetter('loop', val);
            }
        },
        inner_duration: {
            get: function () {
                if (this.inner_properties.duration_type === 0) {
                    return this.inner_properties.duration;
                } else if (this.inner_properties.duration_type === 1) {
                    if (this.default_duration) {
                        return this.default_duration;
                    } else {
                        return Laravel.default_duration;
                    }
                } else {
                    return this.inner_properties.duration_original * this.inner_display_runs;
                }
            },
            set: function (val) {
                if (this.inner_properties.duration_type === 0) {
                    this.inner_properties.duration = val;
                }
            }
        },
        inner_default_duration: {
            get: function () {
                let duration = this.inner_properties.default_duration;
                return duration ? duration : Laravel.default_duration;
            },
            set: function (val) {
                this.inner_properties.default_duration = val;
            }
        },
        inner_display_runs: {
            get: function () {
                return this.inner_properties.duration_type === 2 ? this.innerFieldGetter('display_runs') : 1;
            },
            set: function (val) {
                this.innerFieldSetter('display_runs', val);
            }
        },
        inner_transition_length: {
            get: function () {
                return this.innerFieldGetter('transition_length');
            },
            set: function (val) {
                this.innerFieldSetter('transition_length', val);
            }
        },
        inner_smart_loop_number: {
            get: function () {
                return this.innerFieldGetter('smart_loop_number');
            },
            set: function (val) {
                this.innerFieldSetter('smart_loop_number', val);
            }
        },
        inner_duration_element: function () {
            if (this.inner_properties.type === 'rssfeed') {
                return '?';
            } else if (this.inner_properties.type === 'ppt' || this.inner_properties.type === 'pdf') {
                const page_to = parseInt(!isNaN(this.inner_properties.page_to) && this.inner_properties.page_to !== '' ? this.inner_properties.page_to : this.inner_properties.page_count);
                const page_from = parseInt(!isNaN(this.inner_properties.page_from) && this.inner_properties.page_from !== '' ? this.inner_properties.page_from : 1);
                const interval = parseInt(!isNaN(this.inner_properties.interval) && this.inner_properties.interval !== '' ? this.inner_properties.interval : 11);

                if(interval && page_from && page_to) {
                    return Math.max(interval * (page_to - page_from + 1), 0) ?? '?';
                } else {
                    return '?';
                }
            } else {
                if (this.inner_properties.duration_original === 'unknown' || !this.inner_properties.duration_original) {
                    return '?';
                } else {
                    return this.inner_properties.duration_original;
                }
            }
        },
        validFromPlaceholderLocalized() {
            return localizeDate(this.inner_properties['valid_from_placeholder']);
        },
        validToPlaceholderLocalized() {
            return localizeDate(this.inner_properties['valid_to_placeholder']);
        },
        validFromOrPlaceholder() {
            return this.valid_from || this.inner_properties['valid_from_placeholder'];
        },
        validToOrPlaceholder() {
            return this.valid_to || this.inner_properties['valid_to_placeholder'];
        },
        singleDaySelected() {
            return this.validFromOrPlaceholder && (this.validFromOrPlaceholder == this.validToOrPlaceholder);
        },
        validFromMaxLimit() {
            return this.singleDaySelected ? null : this.validToOrPlaceholder;
        },
        validToMinLimit() {
            return this.singleDaySelected ? null : this.validFromOrPlaceholder;
        },
    },
    watch: {
        value: function () {
            this.inner_properties = this.value;
            this.description = this.inner_properties.description;
        },
    },
    methods: {
        localizeDate,
        updateValidity: function () {
            let valid_from = this.valid_from ?? '';
            let valid_to = this.valid_to ?? '';

            if(valid_from === '') {
                let date_to  = valid_to ? utility.formatDate(valid_to, Laravel.activeDateformat) : null;
                let date_from = valid_from ? utility.formatDate(valid_from, Laravel.activeDateformat) : null;

                if(date_from > date_to) {
                    valid_from = '';
                }
            }
            if(valid_to === '') {
                let date_to  = valid_to ? utility.formatDate(valid_to, Laravel.activeDateformat) : null;
                let date_from = valid_from ? utility.formatDate(valid_from, Laravel.activeDateformat) : null;

                if(date_from > date_to) {
                    valid_to = '';
                }
            }

            this.inner_properties.validity = utility.checkValidity(valid_from, valid_to);
        },
        elementProperties: function () {
            return this.definition;
        },
        changedDescription: function () {
            clearTimeout(this.timeout_id);
            this.timeout_id = setTimeout(this.updateDescription, 1000);
        },
        updateDescription: function () {
            clearTimeout(this.timeout_id);
            this.inner_properties.description = this.description;
        }
    },
    mounted: function () {
        this.transitions = Object.values(Laravel.transitions);
        this.inner_properties = this.value;
        this.description = this.inner_properties.description;
    },
}
</script>
